import { SyncOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Typography } from 'antd';
import { type ReactElement, type ReactNode } from 'react';
import type { DateRange } from '@/types';
import type { BaseShow } from '@/types/api/network';
import { formatDownloadButtonLabel } from './downloadDrawerUtils';
import DownloadShowDateFilters from './DownloadShowDateFilters';

export interface DownloadDrawerSharedSettings {
  shows: string[];
  dateRange: DateRange;
}

interface DownloadDrawerShellProps<TSettings extends DownloadDrawerSharedSettings> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  testId: string;
  shows: BaseShow[];
  downloadSettings: TSettings;
  setDownloadSettings: (settings: TSettings | ((previous: TSettings) => TSettings)) => void;
  selectedFileCount: number;
  isDownloading: boolean;
  onDownload: () => void;
  /** Everything below the filters: each drawer composes its own sections. */
  children?: ReactNode;
}

/**
 * Drawer frame shared by the content performance and audience CSV downloads:
 * title with Cancel and Download actions, show filter and date range.
 */
const DownloadDrawerShell = <TSettings extends DownloadDrawerSharedSettings>({
  isOpen,
  onClose,
  title,
  testId,
  shows,
  downloadSettings,
  setDownloadSettings,
  selectedFileCount,
  isDownloading,
  onDownload,
  children,
}: DownloadDrawerShellProps<TSettings>): ReactElement | null => {
  if (!isOpen) return null;

  return (
    <Drawer
      open
      data-testid={testId}
      size="large"
      onClose={onClose}
      title={
        <Flex wrap align="center" justify="space-between" gap={12}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          <Flex gap={6} style={{ marginInlineStart: 'auto' }}>
            <Button danger type="text" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={selectedFileCount === 0}
              onClick={onDownload}
              loading={isDownloading ? { icon: <SyncOutlined spin /> } : false}
            >
              {formatDownloadButtonLabel(selectedFileCount)}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex vertical style={{ height: '100%' }}>
        <Flex vertical gap={8}>
          <DownloadShowDateFilters
            shows={shows}
            downloadSettings={downloadSettings}
            setDownloadSettings={setDownloadSettings}
          />
          <Typography.Text type="secondary">
            Dates and times in downloads use UTC, so they may differ from your local time
          </Typography.Text>
        </Flex>
        {children}
      </Flex>
    </Drawer>
  );
};

export default DownloadDrawerShell;
